import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Input, Label } from "reactstrap";
import notifications from "../../common/Components/Notifications/Notification";
import { canUseGiRegistry } from "../../helpers/geoEnabled";
import {
  createCert,
  deleteCert,
  getCert,
  listCertKinds,
  updateCert,
  uploadCertImage,
} from "../../services/geoCertApi";
import { parseGeoPath, unwrapGeoPayload } from "../gi-registry/geoPath";
import "../gi-registry/gi-registry.scss";

const readAuthUser = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null") || {};
  } catch {
    return {};
  }
};

const presetKindFromSearch = (search: string) => {
  const kind = new URLSearchParams(search || "").get("kind") || "";
  const value = kind.trim().toUpperCase().replace(/ /g, "_");
  if (value === "SILK_MARK" || value === "HANDLOOM_MARK" || value === "CUSTOM") {
    return value;
  }
  return "SILK_MARK";
};

const FALLBACK_KINDS = [
  { kind: "SILK_MARK", label: "Silk Mark", numberLabel: "Authorisation number" },
  {
    kind: "HANDLOOM_MARK",
    label: "Handloom Mark",
    numberLabel: "Authorisation / registration number",
  },
  { kind: "CUSTOM", label: "Other certification", numberLabel: "Authorisation number" },
];

const CertificationForm = () => {
  const history = useHistory();
  const location = useLocation();
  const path = parseGeoPath(location.pathname, "certifications");
  const certId = path.id;
  const isNew = path.isNew;
  const isView = path.isView;
  const authUser: any = readAuthUser();
  const role = authUser?.users?.role;
  const isSuperAdmin = role === "SUPER_ADMIN";
  const canWrite = canUseGiRegistry(role) && !isSuperAdmin;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [kind, setKind] = useState(isNew ? presetKindFromSearch(location.search) : "SILK_MARK");
  const [kindLabel, setKindLabel] = useState("");
  const [authorisationNumber, setAuthorisationNumber] = useState("");
  const [certificateUrl, setCertificateUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [orgName, setOrgName] = useState("");
  const [kinds, setKinds] = useState(FALLBACK_KINDS);

  const numberLabelFor = (value: string) =>
    kinds.find((row) => row.kind === value)?.numberLabel || "Authorisation number";

  useEffect(() => {
    if (!canUseGiRegistry(role)) {
      history.replace("/dashboard");
      return;
    }
    if (isNew && isSuperAdmin) {
      history.replace("/certifications");
      return;
    }
    if (isNew) {
      setKind(presetKindFromSearch(location.search));
      setLoading(false);
      return;
    }
    if (!certId) {
      history.replace("/certifications");
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    getCert(certId)
      .then((res: any) => {
        if (cancelled) {
          return;
        }
        if (!res?.success) {
          setError(res?.message || "Unable to load certification");
          return;
        }
        const data = unwrapGeoPayload(res) || {};
        setKind(data.kind || "CUSTOM");
        setKindLabel(data.kind === "CUSTOM" ? data.kindLabel || "" : "");
        setAuthorisationNumber(data.authorisationNumber || "");
        setCertificateUrl(data.certificateUrl || "");
        setNotes(data.notes || "");
        setOrgName(data.orgName || "");
      })
      .catch(() => {
        if (!cancelled) {
          setError("Unable to load certification");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [certId, isNew, isSuperAdmin, role, history, location.search]);

  useEffect(() => {
    if (!canUseGiRegistry(role)) {
      return;
    }
    listCertKinds()
      .then((res: any) => {
        const rows = Array.isArray(res?.data) ? res.data : [];
        const mapped = rows
          .map((row: any) => ({
            kind: String(row?.kind || ""),
            label: String(row?.label || ""),
            numberLabel: String(row?.numberLabel || "Authorisation number"),
          }))
          .filter((row: { kind: string; label: string }) => row.kind && row.label);
        if (mapped.length > 0) {
          setKinds(mapped);
        }
      })
      .catch(() => undefined);
  }, [role]);

  const handlePicture = async (file?: File) => {
    if (!file || isView || !canWrite) {
      return;
    }
    setSaving(true);
    try {
      const url = await uploadCertImage(file);
      setCertificateUrl(url);
      notifications({
        title: "Success!",
        message: "Certificate picture uploaded",
        type: "success",
      });
    } catch (err: any) {
      notifications({
        title: "Error!",
        message: err?.message || "Could not upload the certificate picture",
        type: "danger",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!canWrite || isView) {
      return;
    }
    const number = authorisationNumber.trim();
    if (kind === "CUSTOM" && !kindLabel.trim()) {
      notifications({
        title: "Error!",
        message: "Enter the certification name",
        type: "danger",
      });
      return;
    }
    if (!number) {
      notifications({
        title: "Error!",
        message: `${numberLabelFor(kind)} is required`,
        type: "danger",
      });
      return;
    }
    if (!certificateUrl) {
      notifications({
        title: "Error!",
        message: "Upload the certificate picture",
        type: "danger",
      });
      return;
    }
    setSaving(true);
    try {
      const body = {
        kind,
        kindLabel: kind === "CUSTOM" ? kindLabel.trim() : undefined,
        authorisationNumber: number,
        certificateUrl,
        notes: notes.trim() || undefined,
      };
      const res: any = isNew ? await createCert(body) : await updateCert(certId || "", body);
      if (!res?.success) {
        notifications({
          title: "Error!",
          message: res?.message || "Could not save certification",
          type: "danger",
        });
        return;
      }
      notifications({
        title: "Success!",
        message: isNew ? "Certification saved" : "Certification updated",
        type: "success",
      });
      history.push("/certifications");
    } catch {
      notifications({
        title: "Error!",
        message: "Could not save certification",
        type: "danger",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!certId || !canWrite) {
      return;
    }
    if (!window.confirm("Remove this certification from the studio?")) {
      return;
    }
    setSaving(true);
    try {
      const res: any = await deleteCert(certId);
      if (!res?.success) {
        notifications({
          title: "Error!",
          message: res?.message || "Could not delete this certification",
          type: "danger",
        });
        return;
      }
      notifications({
        title: "Success!",
        message: "Certification removed",
        type: "success",
      });
      history.push("/certifications");
    } catch {
      notifications({
        title: "Error!",
        message: "Could not delete this certification",
        type: "danger",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="gi-registry-page">Loading certification…</div>;
  }

  if (error) {
    return (
      <div className="gi-registry-page">
        <div className="gi-registry-toolbar">
          <h2>Certification received</h2>
          <Button className="ResetBtn" onClick={() => history.push("/certifications")}>
            Back to list
          </Button>
        </div>
        <div className="gi-form-banner">{error}</div>
      </div>
    );
  }

  const title = isNew ? "Add certification" : isView ? "Certification" : "Edit certification";
  const readOnly = isView || !canWrite;

  return (
    <div className="gi-registry-page">
      <div className="gi-registry-toolbar">
        <h2>{title}</h2>
        <div className="gi-form-actions" style={{ marginTop: 0 }}>
          <Button className="btn-hastkala-outline" onClick={() => history.push("/certifications")}>
            Back to list
          </Button>
          {isView && canWrite && certId ? (
            <Button className="ResetBtn" onClick={() => history.push(`/certifications/${certId}/edit`)}>
              Edit
            </Button>
          ) : null}
          {!isView && canWrite ? (
            <Button className="ResetBtn" disabled={saving} onClick={handleSave}>
              {saving ? "Saving…" : isNew ? "Save certification" : "Save changes"}
            </Button>
          ) : null}
          {!isNew && canWrite ? (
            <Button className="btn-hastkala-outline" disabled={saving} onClick={handleDelete}>
              Delete
            </Button>
          ) : null}
        </div>
      </div>
      <p className="gi-au-hint">
        Record a government certification held by this studio. Silk Mark and Handloom Mark are
        listed separately. Use Other for certifications such as India Handloom Brand.
        {isSuperAdmin && orgName ? ` Studio: ${orgName}.` : ""}
      </p>
      <div className="gi-form-fields gi-au-form">
        <Label htmlFor="certKind">Certification type</Label>
        <Input
          id="certKind"
          type="select"
          value={kind}
          disabled={readOnly || !isNew}
          onChange={(e) => setKind(e.target.value)}
        >
          {kinds.map((row) => (
            <option key={row.kind} value={row.kind}>
              {row.label}
            </option>
          ))}
        </Input>
        {kind === "CUSTOM" ? (
          <>
            <Label htmlFor="certKindLabel">Certification name</Label>
            <Input
              id="certKindLabel"
              value={kindLabel}
              disabled={readOnly || !isNew}
              onChange={(e) => setKindLabel(e.target.value)}
              placeholder="e.g. India Handloom Brand"
              maxLength={120}
            />
          </>
        ) : null}
        <Label htmlFor="certNumber">{numberLabelFor(kind)}</Label>
        <Input
          id="certNumber"
          value={authorisationNumber}
          disabled={readOnly}
          onChange={(e) => setAuthorisationNumber(e.target.value)}
          placeholder={
            kind === "HANDLOOM_MARK"
              ? "Authorisation or registration number on the certificate"
              : "Authorisation number on the certificate"
          }
          maxLength={64}
        />
        <Label htmlFor="certPicture">Certificate picture</Label>
        {readOnly ? null : (
          <Input
            id="certPicture"
            type="file"
            accept="image/jpeg,image/png,.jpg,.jpeg,.png"
            disabled={saving}
            onChange={(e) => {
              handlePicture(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
        )}
        {certificateUrl ? (
          <>
            <img
              className="gi-au-logo-preview gi-cert-preview"
              src={certificateUrl}
              alt="Certification"
            />
            {readOnly ? (
              <p className="mb-2">
                <a href={certificateUrl} target="_blank" rel="noreferrer">
                  Open picture
                </a>
              </p>
            ) : (
              <p className="mb-2">
                <a href={certificateUrl} target="_blank" rel="noreferrer">
                  Open picture
                </a>
                {" · "}
                <button type="button" className="btn btn-link p-0" onClick={() => setCertificateUrl("")}>
                  Remove
                </button>
              </p>
            )}
          </>
        ) : (
          <p className="gi-au-hint">JPEG or PNG, 8 MB or smaller.</p>
        )}
        <Label htmlFor="certNotes">Notes (optional)</Label>
        <Input
          id="certNotes"
          type="textarea"
          rows={3}
          value={notes}
          disabled={readOnly}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={500}
        />
      </div>
    </div>
  );
};

export default CertificationForm;
